import React from 'react';
import {
  ST_BRAND_TONES,
  ST_NEGATIVE_KEYWORDS,
  ST_TOPICS_TO_AVOID,
} from '../../growth-settings/mockData';
import type { StBrandToneId } from '../../growth-settings/types';
import {
  StAddChip,
  StChip,
  StFieldHeading,
  StSectionCard,
  StToggleRow,
  type StSectionSaveFooter,
} from './SettingsPrimitives';

export interface SettingsContentProps {
  selectedTone: StBrandToneId;
  onSelectTone: (id: StBrandToneId) => void;
  spanishContent?: boolean;
  onSpanishContentChange?: (next: boolean) => void;
  saveFooter?: StSectionSaveFooter;
}

export function SettingsContent({
  selectedTone,
  onSelectTone,
  spanishContent = false,
  onSpanishContentChange,
  saveFooter,
}: SettingsContentProps) {
  return (
    <StSectionCard
      title="Content Preferences"
      description="Guide how ELVA writes your ads, landing pages, and blog articles."
      {...(saveFooter ? { saveFooter } : {})}
    >
      <section>
        <StFieldHeading
          title="Brand Tone"
          sub="How should ELVA write your ad copy and articles?"
        />
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
          {ST_BRAND_TONES.map((tone) => {
            const active = selectedTone === tone.id;
            return (
              <button
                key={tone.id}
                type="button"
                onClick={() => onSelectTone(tone.id)}
                className="cursor-pointer rounded-[var(--r-md)] border px-4 py-4 text-left"
                style={
                  active
                    ? {
                        borderColor: 'var(--success)',
                        background: 'var(--success-tint)',
                      }
                    : { borderColor: 'var(--cream-2)', background: 'var(--cream-1)' }
                }
              >
                <p
                  className="text-[20px] font-semibold leading-tight"
                  style={{ color: active ? 'var(--success)' : 'var(--ink-1)' }}
                >
                  {tone.title}
                </p>
                <p
                  className="mt-2 text-[13px]"
                  style={{ color: active ? 'var(--success)' : 'var(--ink-3)' }}
                >
                  {tone.description}
                </p>
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <StFieldHeading title="Languages" />
        <div className="mt-3">
          <StToggleRow
            label="Spanish content"
            description="Generate Spanish ads, landing pages, and articles for Spanish-speaking patients in your area."
            enabled={spanishContent}
            {...(onSpanishContentChange ? { onEnabledChange: onSpanishContentChange } : {})}
          />
        </div>
      </section>

      <section>
        <StFieldHeading
          title="Topics to Avoid (optional)"
          sub="Phrases or topics ELVA should never use in your ads or content."
        />
        <div className="mt-3 flex flex-wrap gap-2">
          {ST_TOPICS_TO_AVOID.map((topic) => (
            <StChip key={topic}>{topic}</StChip>
          ))}
          <StAddChip label="+ Add" />
        </div>
      </section>

      <section>
        <StFieldHeading
          title="Negative Keywords (optional)"
          sub="Services or keywords ELVA should never bid on in Google Ads."
        />
        <div className="mt-3 flex flex-wrap gap-2">
          {ST_NEGATIVE_KEYWORDS.map((keyword) => (
            <StChip key={keyword}>{keyword}</StChip>
          ))}
          <StAddChip label="+ Add" />
        </div>
      </section>
    </StSectionCard>
  );
}
